import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { CheckCircle2, Download, Lock, CreditCard, Building2 } from 'lucide-react';
import type { PaymentData } from './BalanceAndPay';
interface ConfirmAndDoneProps {
  paymentData: PaymentData;
  onConfirm: () => void;
  isConfirmed: boolean;
  confirmationNumber: string;
}
function formatCurrency(n: number) {
  return n.toLocaleString('en-US', {
    style: 'currency',
    currency: 'USD'
  });
}
function todayLabel() {
  return new Date().toLocaleDateString('en-US', {
    month: 'long',
    day: 'numeric',
    year: 'numeric'
  });
}
function formatDateString(iso: string) {
  // Parse as local date by appending a time component to avoid TZ shifts
  const d = new Date(iso + 'T00:00:00');
  if (isNaN(d.getTime())) return iso;
  return d.toLocaleDateString('en-US', {
    month: 'long',
    day: 'numeric',
    year: 'numeric'
  });
}
export function ConfirmAndDone({
  paymentData,
  onConfirm,
  isConfirmed,
  confirmationNumber
}: ConfirmAndDoneProps) {
  const [isProcessing, setIsProcessing] = useState(false);
  const handleConfirm = () => {
    setIsProcessing(true);
    // Simulate a brief processing delay before showing success
    setTimeout(() => {
      onConfirm();
      setIsProcessing(false);
    }, 700);
  };
  const isCard = paymentData.method === 'card';
  const methodLabel = isCard ? `Card ending in ${paymentData.cardLast4 || '••••'}` : `Bank account ending in ${paymentData.routingLast4 || '••••'}`;
  const methodIcon = isCard ? <CreditCard size={18} className="text-brand-navy" data-id="element-703" /> : <Building2 size={18} className="text-brand-navy" data-id="element-704" />;
  const isPlan = paymentData.paymentType === 'plan';
  const amountLabel = isPlan ? formatCurrency(paymentData.monthlyAmount) : formatCurrency(paymentData.fullAmount);
  if (isConfirmed) {
    return <motion.div initial={{
      opacity: 0,
      scale: 0.96
    }} animate={{
      opacity: 1,
      scale: 1
    }} transition={{
      duration: 0.4
    }} className="text-center" data-id="element-705">
        <div className="bg-white rounded-2xl border border-gray-200 shadow-sm p-8 md:p-12" data-id="element-706">
          <motion.div initial={{
          scale: 0
        }} animate={{
          scale: 1
        }} transition={{
          delay: 0.15,
          type: 'spring',
          stiffness: 200,
          damping: 15
        }} className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6" data-id="element-707">
            <CheckCircle2 size={48} className="text-green-600" data-id="element-708" />
          </motion.div>
          <h1 className="text-2xl md:text-3xl font-bold text-brand-navy mb-3" data-id="element-709">
            {isPlan ? 'Payment plan scheduled' : `Payment of ${amountLabel} submitted`}
          </h1>
          {isPlan ? <p className="text-brand-muted mb-6" data-id="element-710">
              Your first payment of {amountLabel} is scheduled.{' '}
              {paymentData.planEndsLabel}.
            </p> : <p className="text-brand-muted mb-6" data-id="element-711">
              Thank you. A confirmation email has been sent to the address on
              file.
            </p>}

          <div className="bg-brand-gray rounded-lg p-4 inline-block text-left mb-8" data-id="element-712">
            <p className="text-xs font-semibold text-brand-muted uppercase tracking-wider mb-1" data-id="element-713">
              Confirmation Number
            </p>
            <p className="font-bold text-brand-dark font-mono text-lg" data-id="element-714">
              {confirmationNumber}
            </p>
          </div>

          <div className="flex flex-col sm:flex-row gap-3 justify-center" data-id="element-715">
            <button className="inline-flex items-center justify-center gap-2 bg-brand-navy hover:bg-blue-900 text-white px-6 py-3 rounded-lg font-semibold transition-colors" data-id="element-716">
              <Download size={18} data-id="element-717" />
              Download Receipt
            </button>
            <Link to="/portal" className="inline-flex items-center justify-center bg-white border-2 border-gray-200 hover:border-brand-navy text-brand-dark px-6 py-3 rounded-lg font-semibold transition-colors" data-id="element-718">
              Done
            </Link>
          </div>
        </div>
      </motion.div>;
  }
  return <motion.div initial={{
    opacity: 0,
    y: 10
  }} animate={{
    opacity: 1,
    y: 0
  }} transition={{
    duration: 0.4
  }} className="pb-28" data-id="element-719">
      <div className="text-center mb-8" data-id="element-720">
        <h1 className="text-3xl md:text-4xl font-bold text-brand-navy mb-3" data-id="element-721">
          Review and confirm
        </h1>
        <p className="text-brand-muted" data-id="element-722">
          Please verify the details below before submitting your payment.
        </p>
      </div>

      <div className="bg-white rounded-2xl border border-gray-200 shadow-sm overflow-hidden" data-id="element-723">
        <SummaryRow label={isPlan ? 'Monthly amount' : 'Payment amount'} value={<span className="text-2xl font-bold text-brand-navy" data-id="element-725">
              {amountLabel}
            </span>} data-id="element-724" />
        {isPlan && <>
            <SummaryRow label="Plan length" value={<span className="font-semibold text-brand-dark" data-id="element-727">
                  {paymentData.planMonths} payments
                </span>} data-id="element-726" />
            <SummaryRow label="Schedule" value={<span className="text-brand-dark text-right" data-id="element-729">
                  {paymentData.planEndsLabel}
                </span>} data-id="element-728" />
          </>}
        <SummaryRow label="Payment method" value={<span className="flex items-center gap-2 font-semibold text-brand-dark" data-id="element-731">
              {methodIcon}
              {methodLabel}
            </span>} data-id="element-730" />
        <SummaryRow label={isPlan ? 'First payment date' : 'Payment date'} value={<span className="font-semibold text-brand-dark" data-id="element-733">
              {paymentData.payDate ? formatDateString(paymentData.payDate) : todayLabel()}
            </span>} data-id="element-732" />
        <SummaryRow label="Account" value={<span className="text-brand-dark font-mono text-sm" data-id="element-735">
              #102863517
            </span>} last data-id="element-734" />
      </div>

      <div className="mt-6 flex items-start gap-2 text-sm text-brand-muted" data-id="element-736">
        <Lock size={16} className="text-green-600 mt-0.5 shrink-0" data-id="element-737" />
        <p data-id="element-738">
          Your payment information is encrypted and processed securely. By
          confirming, you authorize this {isPlan ? 'payment plan' : 'payment'}{' '}
          to be applied to your account.
        </p>
      </div>

      {/* Sticky CTA */}
      <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 shadow-[0_-4px_12px_rgba(0,0,0,0.04)] z-40" data-id="element-739">
        <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 py-4" data-id="element-740">
          <button onClick={handleConfirm} disabled={isProcessing} className="w-full bg-brand-red hover:bg-red-800 disabled:bg-red-400 disabled:cursor-wait text-white py-3.5 rounded-lg font-bold text-lg transition-colors shadow-sm" data-id="element-741">
            {isProcessing ? 'Processing…' : 'Confirm & Pay'}
          </button>
        </div>
      </div>
    </motion.div>;
}
interface SummaryRowProps {
  label: string;
  value: React.ReactNode;
  last?: boolean;
}
function SummaryRow({
  label,
  value,
  last
}: SummaryRowProps) {
  return <div className={`flex items-center justify-between gap-4 px-6 py-4 ${last ? '' : 'border-b border-gray-100'}`} data-id="element-742">
      <span className="text-sm font-medium text-brand-muted" data-id="element-743">{label}</span>
      <div className="text-right" data-id="element-744">{value}</div>
    </div>;
}